import { monthArrows, vMonthBanner, vNewMonthCard } from './month';
import type { OverviewDto, SettleViewDto } from '../../../shared/api';
import { groupHeader, rowHtml, rowModeToggle, rowStatus, rowView, DEFAULT_ROW_MODE, type RowMode } from './catrow';
import type { NewSplit } from './splits';
import type { NewTip } from './tips';
import { esc, isoDay, money, monthName, shortDate } from '../format';
import { vNewPurchases } from './newcard';
import { vRequestsCard, vSplitNotices } from './splits';
import { vSplitsRow } from './settle';
import { vSilence } from './capture';
import { topBar } from './header';
import { moveCard, oneTapLog } from './items';
import { incomeCard, overviewLeftToBudget } from './income';
import { groupedCategories } from './budget';

export { billLabel } from './catrow';

/** A category row (web/src/views/catrow.ts), with "Paid $250" under a same-amount category with nothing logged yet. */
function catRow(d: OverviewDto, k: OverviewDto['categories'][number], mode: RowMode) {
  const row = rowHtml(rowView(d, k, mode));
  const paid = oneTapLog(k, 'row');
  return paid ? `<div class="crowpaid">${row}<div class="acts">${paid}</div></div>` : row;
}

/** "$425 still due this month". */
export const stillDueText = (cents: number) => `${money(cents)} still due this month`;

/**
 * Still due: one summary row with a chevron; open, the unpaid items by due date ("Oct 22"),
 * then those with no due day under "Any day this month". $0 items are left out. Each row opens
 * its category. The total is the hero's own still-due number (left to spend is unchanged).
 */
export function stillDue(d: OverviewDto, open: boolean): string {
  const items = d.upcoming.filter((u) => u.amountCents > 0);
  if (!items.length && d.totals.upcomingCents <= 0) return '';
  const row = (u: OverviewDto['upcoming'][number], when: string) =>
    `<button class="duerow" data-a="category" data-cat="${esc(u.categoryId)}"><span class="when">${esc(when)}</span><span class="what">${esc(u.name)}${u.autoLog ? ' <small>logs itself</small>' : ''}</span><span class="amt">${money(u.amountCents, 2)}</span></button>`;
  const dated = items.filter((u) => !u.anyDay).sort((a, b) => a.date.localeCompare(b.date) || a.name.localeCompare(b.name));
  const anyDay = items.filter((u) => u.anyDay).sort((a, b) => a.name.localeCompare(b.name));
  const list = open
    ? `<div class="duelist" id="due-list">${dated.map((u) => row(u, shortDate(u.date))).join('')}${
        anyDay.length ? `<p class="duehead">Any day this month</p>${anyDay.map((u) => row(u, '')).join('')}` : ''
      }</div>`
    : '';
  return `<div class="due"><button class="duesum" data-a="due-toggle" aria-expanded="${open}" aria-controls="due-list"><span>${esc(stillDueText(d.totals.upcomingCents))}</span><span class="chev" aria-hidden="true">${open ? '&#9662;' : '&#9656;'}</span></button>${list}</div>`;
}

/**
 * Finish setup: only until the first bank is Working (setup.showCard, from the server), then gone
 * for good; later cards are added from Settings, Cards and banks. "Not now" hides it for 3 days.
 */
export function finishSetup(d: OverviewDto): string {
  if (!d.features.inboundEmail || !d.setup.showCard) return '';
  const started = d.cardsBanks.banks.length > 0;
  const body = started ? FINISH_WAITING : FINISH_START;
  const go = started
    ? '<button class="btn primary" data-a="settings-page" data-page="cards">Cards and banks</button>'
    : '<button class="btn primary" data-a="flow-open">Add a card</button>';
  return `<section class="ask setupcard"><h2>Finish setup</h2><p>${esc(body)}</p>
  <div class="acts">${go}<button class="btn" data-a="setup-snooze">Not now</button></div></section>`;
}

/** Above the first spending group: what the tick on each bar means. */
export const TICK_HINT = 'The tick on each bar marks where spending should be today.';

export const FINISH_START = 'Add your first card so Hush can log purchases by itself.';
export const FINISH_WAITING = 'Waiting for your first alert through Gmail.';

export function vOverview(d: OverviewDto, opts: { dueOpen?: boolean; picks?: Map<string, string>; newSplits?: Map<string, NewSplit>; newTips?: Map<string, NewTip>; newFunds?: Map<string, string>; newItems?: Map<string, string | null>; newFundOn?: Map<string, boolean>; newNotes?: Map<string, string>; newAmounts?: Map<string, number | null>; splits?: SettleViewDto | null; rowMode?: RowMode } = {}): string {
  const { year, month, days, today } = d.month;
  const c = d.totals;
  const mode = opts.rowMode ?? DEFAULT_ROW_MODE;
  const order = { over: 0, ahead: 1, ok: 2, unset: 3 };
  const ratio = (k: OverviewDto['categories'][number]) => k.spentCents / (k.budgetCents || 1);
  const st = (k: OverviewDto['categories'][number]) => rowStatus(d, k);
  const spending = d.categories.filter((k) => k.kind !== 'set_aside');
  const setAside = d.categories.filter((k) => k.kind === 'set_aside');
  const sortRows = (cats: typeof d.categories) => [...cats].sort((a, b) => order[st(a)] - order[st(b)] || ratio(b) - ratio(a));
  // Categories by the household's own groups, in order: one-line headers, two-line rows.
  const blocks = groupedCategories(d).filter((b) => b.cats.length);
  const firstSpending = blocks.findIndex((b) => b.group.kind === 'spending');
  const groupsHtml = blocks
    .map(
      (b, i) =>
        `${groupHeader(d, b.group, b.cats, mode)}${i === firstSpending ? `<p class="hint">${esc(TICK_HINT)}</p>` : ''}<div class="list">${(b.group.kind === 'set_aside' ? b.cats : sortRows(b.cats))
          .map((k) => catRow(d, k, mode))
          .join('')}</div>`,
    )
    .join('');
  // Planned / Spent / Remaining: only the primary number on each row and group header changes.
  const toggle = blocks.length ? rowModeToggle(mode) : '';

  // A new budget starts with every category at $0: nothing is tracked until amounts are set.
  const setAmounts = spending.every((k) => k.budgetCents <= 0)
    ? `<section class="ask"><h2>Set your monthly amounts</h2>
  <p class="hint">Every category is at $0, so Hush is not tracking anything yet. A category counts toward insights and left to spend once it has an amount.</p>
  <div class="acts"><button class="btn primary" data-a="tab" data-tab="budget">Set amounts</button></div></section>`
    : '';
  // Asked in both modes: Hush cannot tell on its own whether a new address is the user's bank.
  const newSenders = d.alertSenders.filter((s) => s.status === 'pending');
  const senderAsk = newSenders.length
    ? `<section class="ask"><h2>New alert sender</h2>${newSenders
        .map(
          (s) => `<div class="askrow"><div class="askhead"><span class="sender">New sender: ${esc(s.sender)}. Add it?</span></div>
  <p class="hint">${
    s.held
      ? `It sent an alert for ${esc(s.held.merchant ?? 'an unnamed merchant')}, ${money(s.held.amountCents, 2)}${s.held.date ? ` on ${shortDate(s.held.date)}` : ''}. Hush has not logged it. Add it to log this purchase and future alerts from this address.`
      : 'Add it if it is another bank or card of yours.'
  }</p>
  <div class="chips"><button class="chip" data-a="sender-allow" data-sender="${esc(s.sender)}">Add it</button><button class="chip" data-a="sender-decline" data-sender="${esc(s.sender)}">Not mine</button></div></div>`,
        )
        .join('')}</section>`
    : '';
  const finish = finishSetup(d);
  // Move your money: transfers due by today, not done. "Not now" hides it for 3 days.
  const move = d.moveMoney.showCard ? moveCard(d.moveMoney.items, { snooze: true }) : '';
  // Money set aside is not spending: one line under left to spend, never part of it.
  const setAsideLine = setAside.length
    ? `<p class="sub setaside">Set aside: ${money(c.setAsideCents)} of ${money(c.setAsidePlannedCents)} done</p>`
    : '';
  // The mode lives in Settings; only sample data is labeled here.
  // The title opens the month list; the arrows step through months (views/month.ts).
  const sub = d.month.isCurrent ? `Day ${today} of ${days}` : `${days} days`;
  return `${topBar(monthName(year, month), sub, `${d.isSample ? '<span class="pill">Sample data</span>' : ''}${monthArrows(d)}`, 'month-list')}
  ${vMonthBanner(d)}
  ${finish}
  <section class="hero"><p class="lab">Left to spend</p><p class="big ${c.leftCents < 0 ? 'neg' : ''}">${money(c.leftCents)}</p>
  <p class="sub">${money(c.spentCents)} spent so far.</p>${setAsideLine}${overviewLeftToBudget(d)}${stillDue(d, opts.dueOpen ?? false)}</section>
  ${vNewMonthCard(d)}
  ${vRequestsCard(d)}
  ${vSplitNotices(d)}
  ${vNewPurchases(d, isoDay(d.month.key, Math.max(1, today)), opts.picks, opts.newSplits, opts.newTips, opts.newFunds, opts.newItems, opts.newFundOn, opts.newNotes, opts.newAmounts)}
  ${vSplitsRow(d, opts.splits ?? null)}
  ${move}
  ${incomeCard(d)}
  ${vSilence(d, true)}
  ${senderAsk}
  ${setAmounts}
  ${toggle}
  ${groupsHtml}`;
}
